export type Block =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "takeaway"; title: string; text: string };

export interface BlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  /** ISO date used for schema + sorting */
  date: string;
  /** Human readable date shown in the UI */
  dateLabel: string;
  readingTime: string;
  excerpt: string;
  intro: string;
  icon: "coins" | "clipboard" | "boxes" | "building" | "shield" | "calendar";
  ctaLabel: string;
  blocks: Block[];
}

/**
 * Inline links inside block text use markdown-style syntax: [label](/path)
 * They are rendered by BlogPostLayout.
 */
export const blogPosts: BlogPost[] = [
  {
    slug: "10-ways-to-make-rubbish-removal-day-easier",
    title: "10 Ways to Make Rubbish Removal Day Easier",
    metaTitle: "10 Ways to Make Rubbish Removal Day Easier | OZ WASTE CO.",
    metaDescription:
      "Ten practical tips from a Melbourne rubbish removal crew to keep pickup day fast and simple, from clearing access to sorting your pile before we arrive.",
    category: "Pickup Day Tips",
    date: "2026-07-29",
    dateLabel: "29 July 2026",
    readingTime: "5 min read",
    icon: "calendar",
    excerpt:
      "Rubbish removal day runs on preparation. These ten habits keep the load moving, the crew productive and your bill lower.",
    intro:
      "Most of the friction on a rubbish removal job comes from small things nobody decided in advance: where the ute and trailer park, what is actually going, and what is staying. Sort those out early and the pickup becomes a quick, clean job.",
    ctaLabel: "Book a Rubbish Pickup",
    blocks: [
      { type: "h2", text: "1. Put everything in one place" },
      {
        type: "p",
        text: "The single biggest time saver is a pile. If the rubbish is spread across a garage, a back shed and two rooms, the crew spends the first stretch walking rather than loading. One staging area near the driveway does most of the work for you.",
      },
      { type: "h2", text: "2. Keep every access path clear" },
      {
        type: "p",
        text: "Hallways, side gates, driveways and the run from the pile to the trailer should be free of cars, bins, bikes and pot plants before we arrive. Our team carries heavy items backwards more often than you would think, and a clear run is faster and safer.",
      },
      { type: "h2", text: "3. Sort keep from go" },
      {
        type: "p",
        text: "Mark anything that is staying, or move it into a separate room. A quick label on a box beats a mid-load conversation about whether the bookshelf is going.",
      },
      {
        type: "takeaway",
        title: "Quick win",
        text: "Blue tape on anything staying. The crew never has to ask twice, and nothing leaves that you wanted to keep.",
      },
      { type: "h2", text: "4. Send photos when you book" },
      {
        type: "p",
        text: "A few photos on your phone tell us the volume, the access and whether we need extra hands. That is how you get an accurate price instead of a guess. Upload them straight into the [quote form](/#quote).",
      },
      { type: "h2", text: "5. Pull apart the bulky stuff if you can" },
      {
        type: "ul",
        items: [
          "Flat pack shelving and bed bases",
          "Table legs and desk frames",
          "Trampoline frames and swing sets",
          "Empty drawers out of dressers",
          "Break down cardboard boxes",
          "Bag loose garden and household waste",
        ],
      },
      { type: "h2", text: "6. Keep children and pets somewhere safe" },
      {
        type: "p",
        text: "Open gates, trolleys and heavy furniture do not mix with curious toddlers or nervous dogs. One closed-off room with food and water is enough.",
      },
      { type: "h2", text: "7. Flag anything hazardous early" },
      {
        type: "p",
        text: "Paint, chemicals, gas bottles, asbestos and car batteries need specific handling and cannot simply go on the trailer. Tell us at booking and we will point you to the right disposal route rather than turning up and leaving it behind.",
      },
      { type: "h2", text: "8. Think about parking" },
      {
        type: "p",
        text: "A ute and trailer parked 40 metres away adds time to every trip. Where you can, hold the space out the front the night before, especially in tight inner-city streets.",
      },
      { type: "h2", text: "9. Mention stairs, lifts and steep driveways" },
      {
        type: "p",
        text: "Fourth-floor walk-ups, narrow laneways and long carries change the crew size and the time on site. Mention it when you book and the job runs to plan. Our [move-out clear-outs](/move-out-clear-outs) team plans lift and dock access as part of the booking.",
      },
      { type: "h2", text: "10. Keep valuables out of the pile" },
      {
        type: "p",
        text: "Documents, keys, jewellery and hard drives have a habit of ending up in a box that looks like junk. Take a last look through the pile before we load.",
      },
      {
        type: "takeaway",
        title: "The short version",
        text: "One pile, clear access, nothing hazardous hidden in it, and photos sent up front. That solves about 80% of pickup-day friction.",
      },
      { type: "h2", text: "Then let the crew work" },
      {
        type: "p",
        text: "The most useful thing you can do on the day is stay contactable for quick decisions. If you would rather hand it over entirely, our [household rubbish removal](/household-rubbish-removal) and [property clear-outs](/property-clear-outs) crews handle everything from sorting to sweep-up.",
      },
    ],
  },
  {
    slug: "how-to-choose-a-reliable-rubbish-removal-company-in-melbourne",
    title: "How to Choose a Reliable Rubbish Removal Company in Melbourne",
    metaTitle: "How to Choose a Rubbish Removal Company in Melbourne | OZ WASTE CO.",
    metaDescription:
      "What to check before booking a Melbourne rubbish removal service: licensing, insurance, written quotes, disposal practices, reviews and the red flags to avoid.",
    category: "Choosing a Provider",
    date: "2026-07-17",
    dateLabel: "17 July 2026",
    readingTime: "7 min read",
    icon: "shield",
    excerpt:
      "Licensing, insurance, written quotes and responsible disposal: a practical framework for comparing Melbourne rubbish removal companies before you book.",
    intro:
      "Most complaints about rubbish removal trace back to something that was never clarified before the booking: the volume, the price, or where the load actually ends up. This guide covers what to check, what to ask, and which warning signs are worth walking away from.",
    ctaLabel: "Request a Quote From OZ WASTE CO.",
    blocks: [
      { type: "h2", text: "Start with insurance and licensing" },
      {
        type: "p",
        text: "Ask two questions: do you hold public liability cover, and are you licensed to transport and dispose of waste? Public liability protects your property, driveways, lifts, walls and common areas. Licensing is what stops your load being tipped somewhere it should not be.",
      },
      {
        type: "p",
        text: "An operator who cannot answer clearly, or who says \"we are careful so we do not need it\", is telling you something useful.",
      },
      {
        type: "takeaway",
        title: "Ask for it in writing",
        text: "Cover and licensing should appear on the quote or be confirmed by email. A verbal assurance is difficult to rely on later.",
      },
      { type: "h2", text: "Read reviews properly" },
      {
        type: "ul",
        items: [
          "Look at recent reviews, not just the overall score",
          "Read the three star reviews, as they are usually the most honest",
          "Check whether the company replies to criticism, and how",
          "Be sceptical of dozens of five star reviews posted in the same week",
          "Look for detail: named suburbs, specific loads, real timelines",
        ],
      },
      { type: "h2", text: "Insist on a written quote" },
      {
        type: "p",
        text: "A written quote should state the load size, what is included, the tip fees, the labour and what happens if the pile turns out bigger than expected. If any of those are missing, the final invoice is a guess.",
      },
      {
        type: "p",
        text: "Our full breakdown of Melbourne pricing is in [how much rubbish removal costs in Melbourne](/blog/how-much-does-rubbish-removal-cost-in-melbourne).",
      },
      { type: "h2", text: "Ask where the rubbish goes" },
      {
        type: "p",
        text: "A responsible operator sorts the load and takes recoverable metal, cardboard, e-waste and green waste to the right facility rather than sending everything to landfill. Illegal dumping is still traced back to the property owner surprisingly often, so this is worth asking about directly.",
      },
      { type: "h2", text: "Check the vehicle and the equipment" },
      {
        type: "p",
        text: "Trolleys, straps, gloves, tarps and a properly caged trailer make a measurable difference to speed and safety. Ask what vehicle is allocated to your job. An undersized trailer means a second run, and a second run is billable.",
      },
      { type: "h2", text: "Experience with your type of job" },
      {
        type: "p",
        text: "A single couch pickup in Brunswick and a full deceased-estate clear-out in Frankston are different jobs. Specialist experience matters most for [property clear-outs](/property-clear-outs), [office and commercial rubbish](/commercial-rubbish-removal) and [garage and shed clear-outs](/garage-shed-clear-outs).",
      },
      { type: "h2", text: "Judge the communication" },
      {
        type: "p",
        text: "How a company communicates before the booking is a fair preview of how they communicate on the day. Fast replies, clear answers and a named contact are good signs. Vague answers rarely improve after payment.",
      },
      { type: "h2", text: "Hidden fees to ask about directly" },
      {
        type: "ul",
        items: [
          "Travel or call out time and how it is calculated",
          "Stair and long carry charges",
          "Heavy item fees for pianos, safes and treadmills",
          "Tip and landfill levies: included or added later",
          "Weekend, public holiday and after hours rates",
          "Minimum load charges",
          "GST: quoted inclusive or exclusive",
        ],
      },
      { type: "h2", text: "Understand the cancellation terms" },
      {
        type: "p",
        text: "Settlement dates move and tenants leave late. Ask how much notice is required to reschedule without a fee, and whether a deposit is refundable. Reasonable companies have a clear policy and will tell you what it is.",
      },
      { type: "h2", text: "Ask who is actually turning up" },
      {
        type: "p",
        text: "Some operators pass jobs to whoever is free. That is not automatically bad, but you should know whether the crew is trained, insured under the same policy, and accountable to the company you booked.",
      },
      {
        type: "takeaway",
        title: "Red flags",
        text: "No written quote, cash only, no ABN, no insurance detail, no answer about disposal, a price far below every other quote, or pressure to pay a large deposit immediately.",
      },
      { type: "h2", text: "Questions worth asking before you book" },
      {
        type: "ol",
        items: [
          "What is included in the price, and are tip fees covered?",
          "Is there a minimum load charge?",
          "What insurance and waste licensing do you hold?",
          "How many crew and what vehicle size are allocated to my job?",
          "What happens if the pile is bigger than the photos suggested?",
          "Do you charge extra for stairs, long carries or heavy items?",
          "Where does the load go, and what gets recycled?",
          "What is your reschedule and cancellation policy?",
        ],
      },
      { type: "h2", text: "Comparing quotes fairly" },
      {
        type: "p",
        text: "Line the quotes up on the same basis: load size, labour, tip fees and inclusions. The cheapest headline price can easily end up the most expensive job if it comes with one person instead of two and a trailer that needs a second run. For a written, itemised quote, [request one here](/#quote) or [get in touch](/contact) with any questions first.",
      },
    ],
  },
  {
    slug: "rubbish-removal-from-melbourne-apartments",
    title: "Rubbish Removal From Melbourne Apartments: What You Need to Know",
    metaTitle: "Apartment Rubbish Removal in Melbourne | OZ WASTE CO.",
    metaDescription:
      "Lift bookings, loading docks, body corporate rules and building insurance: how rubbish removal from a Melbourne apartment actually works.",
    category: "Apartments",
    date: "2026-07-05",
    dateLabel: "5 July 2026",
    readingTime: "6 min read",
    icon: "building",
    excerpt:
      "Apartment rubbish removal is a building job as much as a lifting job. Lifts, docks and body corporate paperwork decide how the day runs.",
    intro:
      "Getting an old couch out of a twelfth-floor apartment in Southbank is a completely different exercise to a kerbside pickup in Reservoir. The building sets the rules, and the buildings that run tightest are the ones that need the most notice.",
    ctaLabel: "Plan Your Apartment Clear-Out",
    blocks: [
      { type: "h2", text: "Book the lift before anything else" },
      {
        type: "p",
        text: "Most Melbourne towers require a service lift booking for any bulky item removal, often a week or more ahead. Windows are usually two to four hours, and if you miss yours the next slot can be days away.",
      },
      { type: "h2", text: "Ask building management the right questions" },
      {
        type: "ul",
        items: [
          "Which lift is the service lift, and what are its dimensions?",
          "Is a loading dock booking separate from the lift booking?",
          "What is the maximum vehicle height for the dock?",
          "Are there restrictions on weekends or after hours?",
          "Is a bond or deposit required?",
          "Do contractors need to sign in, and where?",
        ],
      },
      {
        type: "takeaway",
        title: "Certificate of currency",
        text: "Most body corporates ask for a certificate of currency showing public liability cover before contractors are allowed on site. Request it from us early and hand it to building management with your lift booking.",
      },
      { type: "h2", text: "Understand the loading dock height limit" },
      {
        type: "p",
        text: "Dock clearances of 2.1 to 3.5 metres are common, and plenty of basement carparks are tighter than that. Tell us the clearance and we will bring a vehicle that fits, or plan a kerbside transfer instead of discovering the problem on the day.",
      },
      { type: "h2", text: "Protect the common areas" },
      {
        type: "p",
        text: "Lift blankets, corner guards and floor protection are standard on our apartment jobs. Damage to a lift interior or a lobby wall is the fastest way to lose a bond, and it is entirely avoidable.",
      },
      { type: "h2", text: "Know what the building bin cannot take" },
      {
        type: "p",
        text: "Apartment waste rooms are sized for household bags, not mattresses, bed frames or renovation debris. Dumping bulky items in the bin room usually earns a body corporate fine, which is why a booked pickup is almost always cheaper than the shortcut.",
      },
      { type: "h2", text: "Time the job around the building, not the traffic" },
      {
        type: "p",
        text: "Mid-morning on a weekday is usually the easiest window: peak-hour dock congestion has cleared and building managers are on site if something needs sorting.",
      },
      {
        type: "takeaway",
        title: "Plan for lift delays",
        text: "Residents call the service lift too. Build a buffer into your booking window rather than assuming the lift is yours alone for the whole slot.",
      },
      { type: "h2", text: "Moving out at the same time?" },
      {
        type: "p",
        text: "End-of-lease jobs usually combine a clear-out with a clean. Our [move-out clear-outs](/move-out-clear-outs) service handles the leftover furniture and rubbish so the property is inspection-ready, and [furniture removal](/furniture-removal) covers single bulky items on their own.",
      },
      { type: "h2", text: "Give us the building details up front" },
      {
        type: "p",
        text: "Floor, lift access, dock clearance, distance from the lift to the door and any body corporate paperwork. Send those with your [quote request](/#quote) and the price you get back will be the price you pay.",
      },
    ],
  },
  {
    slug: "how-to-sort-a-rubbish-pile-without-wasting-time",
    title: "How to Sort a Rubbish Pile Without Wasting Time",
    metaTitle: "How to Sort Rubbish Before a Pickup | OZ WASTE CO. Melbourne",
    metaDescription:
      "A room-by-room method for sorting household rubbish before a removal: what to separate, what can be recycled, and what needs special handling.",
    category: "Sorting & Recycling",
    date: "2026-06-18",
    dateLabel: "18 June 2026",
    readingTime: "7 min read",
    icon: "boxes",
    excerpt:
      "Sorting is where most people lose a weekend. A simple four-pile system, done room by room, gets a house cleared far faster.",
    intro:
      "Sorting rubbish stalls when every item becomes a separate decision. A fixed system removes the deliberation: four piles, one room at a time, and nothing goes back where it came from.",
    ctaLabel: "Get Help Clearing It Out",
    blocks: [
      { type: "h2", text: "Use four piles and only four" },
      {
        type: "ul",
        items: [
          "Keep: goes back into the house, today",
          "Donate or sell: still usable by someone else",
          "Recycle: metal, cardboard, e-waste, green waste",
          "Rubbish: everything else, ready for pickup",
        ],
      },
      { type: "h2", text: "Work one room at a time" },
      {
        type: "p",
        text: "Finishing a room gives you a visible result and stops half-sorted piles migrating between spaces. Start with the room you use least: the garage, the shed or the spare bedroom.",
      },
      { type: "h2", text: "Start with the obvious rubbish" },
      {
        type: "p",
        text: "Broken furniture, empty packaging, expired chemicals and anything water damaged. Clearing the certain items first exposes how much is actually left to decide on, and it is usually less than it looked.",
      },
      {
        type: "takeaway",
        title: "The twelve month rule",
        text: "If it has not been used in twelve months, is not seasonal and is not sentimental, it goes. Do not re-litigate the same item twice in one day.",
      },
      { type: "h2", text: "Know what can be recovered" },
      {
        type: "ul",
        items: [
          "Scrap metal: bed frames, BBQs, tools, old bikes",
          "Cardboard: flattened, not wet",
          "E-waste: TVs, monitors, computers, cabling",
          "Green waste: branches, clippings, soil-free garden waste",
          "Whitegoods: fridges, washers and dryers",
          "Timber: clean, untreated offcuts",
        ],
      },
      {
        type: "p",
        text: "We sort these out of the load rather than sending it all to landfill. See [household rubbish removal](/household-rubbish-removal) for what a typical job covers.",
      },
      { type: "h2", text: "Keep hazardous items separate" },
      {
        type: "p",
        text: "Paint, solvents, gas bottles, car batteries, oils and anything containing asbestos need dedicated disposal and should never go into a general pile. Set them aside in one clearly marked spot and flag them when you book.",
      },
      { type: "h2", text: "Bag the small stuff" },
      {
        type: "p",
        text: "Loose items are the slowest thing to load. Heavy-duty bags for soft rubbish, boxes for anything sharp, and a tied bundle for timber or garden waste. Bagged loads pack tighter, which means less volume and a lower price.",
      },
      {
        type: "takeaway",
        title: "Tie off and label",
        text: "Mark bags containing glass or anything sharp. It protects the crew and takes ten seconds.",
      },
      { type: "h2", text: "Give donations a real deadline" },
      {
        type: "p",
        text: "The donate pile is where clear-outs die. Book the charity pickup or set a drop-off day before you start sorting, and anything not collected by that day joins the rubbish pile.",
      },
      { type: "h2", text: "Stage the pile near the access point" },
      {
        type: "p",
        text: "Driveway, garage roller door or the side gate, whichever gives the shortest carry to the street. This is the single easiest way to cut time on site.",
      },
      { type: "h2", text: "When to stop sorting and just book" },
      {
        type: "p",
        text: "If the volume is beyond a weekend, or the property is a full [garage or shed clear-out](/garage-shed-clear-outs), it is usually cheaper to have us sort and load on site. [Send us a few photos](/#quote) and we will tell you what it involves.",
      },
    ],
  },
  {
    slug: "the-complete-house-clear-out-checklist",
    title: "The Complete House Clear-Out Checklist",
    metaTitle: "The Complete House Clear-Out Checklist | OZ WASTE CO. Melbourne",
    metaDescription:
      "A week-by-week checklist for clearing out a Melbourne house: what to sort, what to book, what needs special disposal and what to do on pickup day.",
    category: "Clear-Out Planning",
    date: "2026-06-02",
    dateLabel: "2 June 2026",
    readingTime: "8 min read",
    icon: "clipboard",
    excerpt:
      "A structured checklist that takes a house clear-out from first sort to final sweep, without the last-minute panic.",
    intro:
      "Whether it is an end-of-lease clean-up, a renovation or a deceased estate, a house clear-out is a sequence, not a single day. Working through it in order keeps the volume, the cost and the stress under control.",
    ctaLabel: "Book Your Clear-Out",
    blocks: [
      { type: "h2", text: "Four weeks out" },
      {
        type: "ol",
        items: [
          "Walk the property and list every space, including the shed, roof cavity and under the house",
          "Decide what is staying, being sold, donated or removed",
          "Photograph each area for quotes",
          "Get written quotes and compare them on the same basis",
          "Check for anything hazardous: paint, chemicals, gas bottles, asbestos",
        ],
      },
      {
        type: "takeaway",
        title: "Sort before you quote",
        text: "The less volume in the pile, the smaller the load and the lower the price. An hour of sorting usually saves more than it costs.",
      },
      { type: "h2", text: "Three weeks out" },
      {
        type: "ol",
        items: [
          "List anything sellable while there is still time for it to move",
          "Book charity collections for usable furniture",
          "Confirm your pickup date and window",
          "If it is an apartment, book the service lift and loading dock",
          "Ask for a certificate of currency if the body corporate requires one",
        ],
      },
      { type: "h2", text: "Two weeks out" },
      {
        type: "ol",
        items: [
          "Start room-by-room sorting with the least-used spaces",
          "Break down flat pack furniture and bed frames",
          "Separate scrap metal, e-waste and cardboard",
          "Empty and defrost any fridge or freezer being removed",
          "Set aside hazardous items in one marked location",
        ],
      },
      { type: "h2", text: "One week out" },
      {
        type: "ol",
        items: [
          "Confirm the booking, the access and the parking",
          "Move the pile toward the driveway or garage",
          "Clear the access path of cars, bins and obstacles",
          "Do a final pass through drawers, cupboards and boxes",
          "Arrange somewhere safe for pets on the day",
        ],
      },
      { type: "h2", text: "The day before" },
      {
        type: "ul",
        items: [
          "Hold the parking space out the front",
          "Tape or label anything that is staying",
          "Charge your phone and keep the booking contact handy",
          "Check the shed, balcony and side yard one last time",
        ],
      },
      { type: "h2", text: "Pickup day" },
      {
        type: "ol",
        items: [
          "Walk the crew through the property and confirm the scope",
          "Point out anything fragile, heavy or hazardous",
          "Stay contactable for quick decisions",
          "Do a final walkthrough before the crew leaves",
          "Confirm the invoice matches the quote",
        ],
      },
      {
        type: "takeaway",
        title: "The final property check",
        text: "Roof cavity, under the house, behind the shed, the letterbox and the top shelf of every cupboard. That is where the last of it always is.",
      },
      { type: "h2", text: "After the clear-out" },
      {
        type: "p",
        text: "Sweep the emptied spaces while they are still empty, take photos for the landlord or agent, and get any remaining repairs booked. For end-of-lease jobs, see [move-out clear-outs](/move-out-clear-outs); for whole properties, see [property clear-outs](/property-clear-outs).",
      },
      { type: "h2", text: "If the scope is bigger than expected" },
      {
        type: "p",
        text: "Large estates, hoarded properties and post-renovation sites are worth handing over entirely. [Send through photos](/#quote) and we will scope it properly before anyone starts lifting.",
      },
    ],
  },
  {
    slug: "how-much-does-rubbish-removal-cost-in-melbourne",
    title: "How Much Does Rubbish Removal Cost in Melbourne?",
    metaTitle: "How Much Does Rubbish Removal Cost in Melbourne? | OZ WASTE CO.",
    metaDescription:
      "What drives rubbish removal pricing in Melbourne: load size, labour, tip fees, access and heavy items, plus how to keep the cost down.",
    category: "Pricing",
    date: "2026-05-15",
    dateLabel: "15 May 2026",
    readingTime: "6 min read",
    icon: "coins",
    excerpt:
      "Rubbish removal is priced on volume, labour and tip fees. Here is what actually moves the number, and how to keep it low.",
    intro:
      "Rubbish removal prices in Melbourne vary widely because the jobs vary widely. Understanding the three components of a quote makes it far easier to compare operators and to spot a number that will not hold.",
    ctaLabel: "Get a Free Rubbish Removal Quote",
    blocks: [
      { type: "h2", text: "What you are actually paying for" },
      {
        type: "ul",
        items: [
          "Volume: how much space the load takes on the trailer",
          "Labour: how long it takes to carry and load it",
          "Tip fees: landfill levies and disposal charges by waste type",
        ],
      },
      {
        type: "p",
        text: "Everything else, access, heavy items, sorting, is a variation on those three.",
      },
      { type: "h2", text: "Load size is the main lever" },
      {
        type: "p",
        text: "Most operators, including us, price by fraction of a load: a quarter, half, three quarter or full load. Compacting and breaking down bulky items genuinely reduces the volume you pay for, which is why flat-packing that old wardrobe is worth the fifteen minutes.",
      },
      {
        type: "takeaway",
        title: "Ask how volume is measured",
        text: "Cubic metres, trailer fractions or truck loads all mean different things. Get the measure in writing so two quotes can be compared honestly.",
      },
      { type: "h2", text: "Labour and access" },
      {
        type: "p",
        text: "A kerbside pile takes a fraction of the time of the same volume carried down three flights of stairs or up a steep driveway. Long carries, lift waits and tight laneways all add labour, so mention them when you book rather than absorbing a surprise on the invoice.",
      },
      { type: "h2", text: "Tip fees and waste type" },
      {
        type: "p",
        text: "Victoria's landfill levy means general waste costs more to dispose of than clean, sorted material. Separating scrap metal, cardboard and green waste reduces what goes to landfill, which is better environmentally and often cheaper.",
      },
      { type: "h2", text: "Items that carry a surcharge" },
      {
        type: "ul",
        items: [
          "Mattresses, which have a dedicated disposal fee",
          "Tyres and car parts",
          "Fridges and air conditioners requiring degassing",
          "E-waste, which cannot go to landfill in Victoria",
          "Pianos, safes and other very heavy single items",
          "Anything requiring hazardous handling",
        ],
      },
      { type: "h2", text: "What tends to inflate a quote" },
      {
        type: "ol",
        items: [
          "Underestimating the pile when describing it over the phone",
          "Rubbish spread across multiple areas of the property",
          "Blocked driveways and no nearby parking",
          "Hazardous items discovered on site",
          "Same-day bookings during peak periods",
        ],
      },
      { type: "h2", text: "Seven ways to bring the price down" },
      {
        type: "ol",
        items: [
          "Sort and reduce before you book",
          "Stage everything in one pile near the driveway",
          "Break down bulky furniture to cut volume",
          "Separate metal, cardboard and green waste",
          "Send accurate photos so the quote is right first time",
          "Book midweek where your dates allow",
          "Combine several small jobs into one pickup",
        ],
      },
      {
        type: "takeaway",
        title: "Cheapest is not always cheapest",
        text: "A low headline price with tip fees added later, or a trailer too small for the job, routinely ends up costing more than a complete quote.",
      },
      { type: "h2", text: "Getting an accurate number" },
      {
        type: "p",
        text: "The fastest route to a real price is photos. Send a few shots of the pile and the access, and we will come back with a written quote that includes labour and tip fees. Start with the [quote form](/#quote), or read about [household rubbish removal](/household-rubbish-removal) and [furniture removal](/furniture-removal) first.",
      },
    ],
  },
];

export const postsNewestFirst = [...blogPosts].sort((a, b) => (a.date < b.date ? 1 : -1));

export const getPost = (slug: string) => blogPosts.find((p) => p.slug === slug);

export const legacyBlogSlugRedirects: Record<string, string> = {
  "stress-free-moving-day": "10-ways-to-make-rubbish-removal-day-easier",
  "choosing-a-removalist-melbourne": "how-to-choose-a-reliable-rubbish-removal-company-in-melbourne",
  "apartment-moving-melbourne": "rubbish-removal-from-melbourne-apartments",
  "how-to-pack-for-moving": "how-to-sort-a-rubbish-pile-without-wasting-time",
  "moving-house-checklist": "the-complete-house-clear-out-checklist",
  "removalist-costs-melbourne": "how-much-does-rubbish-removal-cost-in-melbourne",
};
